import type { AuthUser, OrgInfo, OrgRef, TenantInfo, TenantTheme } from './authSlice';
import type { OrgFeatureRow } from '../utils/moduleLabels';

/** Raw `initApp` / `refreshSession` GraphQL payload — org/tenant/theme/user are JSON scalars. */
export interface InitAppPayload {
  accessToken: string;
  refreshToken: string;
  featureKeys: string[];
  orgFeatures?: OrgFeatureRow[] | null;
  socketRooms: string[];
  org?: Record<string, unknown> | null;
  tenant?: Record<string, unknown> | null;
  theme?: Record<string, unknown> | null;
  user?: Record<string, unknown> | null;
  orgs?: OrgRef[] | null;
  /** Current org membership id (preferred over deriving from orgs). */
  membershipId?: string | number | null;
  pendingTaskCount?: number;
  operationsLocked?: boolean;
}

export interface InitAppFallbacks {
  tenant?: TenantInfo | null;
  theme?: TenantTheme | null;
  user?: AuthUser | null;
}

/** Narrows an init/refresh payload into the `setAuthFromInit` argument. */
export function mapInitAppPayload(
  payload: InitAppPayload,
  fallbacks: InitAppFallbacks = {},
) {
  const org = (payload.org as OrgInfo | null) ?? null;
  const orgs = payload.orgs ?? null;
  const orgId = String(org?.id ?? org?.orgId ?? '');
  const fromOrgs = orgs?.find((o) => String(o.orgId) === orgId)?.membershipId;
  const membershipId =
    payload.membershipId != null && payload.membershipId !== ''
      ? String(payload.membershipId)
      : fromOrgs != null
        ? String(fromOrgs)
        : null;

  return {
    accessToken: payload.accessToken,
    refreshToken: payload.refreshToken,
    featureKeys: payload.featureKeys,
    orgFeatures: payload.orgFeatures ?? [],
    socketRooms: (payload.socketRooms ?? []).filter(Boolean) as string[],
    org,
    tenant: (payload.tenant as TenantInfo | null) ?? fallbacks.tenant ?? null,
    theme: (payload.theme as TenantTheme | null) ?? fallbacks.theme ?? null,
    user: (payload.user as AuthUser | null) ?? fallbacks.user ?? null,
    orgs,
    membershipId,
    pendingTaskCount: payload.pendingTaskCount ?? 0,
    operationsLocked: payload.operationsLocked ?? false,
  };
}
